import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { HreflangTags } from "../components/SeoHreflang";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box, Container, Typography, Button, Paper, Chip, Stack,
  Divider, CircularProgress, Alert, Avatar,
} from "@mui/material";
import {
  CheckCircle, School, ArrowBack, LockOutlined,
  CreditCard, ArrowForward, AutoAwesome,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { apiClient } from "../services/authService";
import { LbpSettings, DEFAULT_LBP_SETTINGS, getLbpSettings, formatLbpOnly } from "../utils/currency";

const BLUE  = "#1565C0";
const NAVY  = "#0A1628";
const MUTED = "#78909C";
const BG    = "#F0F7FF";

interface CourseInfo {
  _id: string;
  title: string;
  description: string;
  thumbnail?: string;
  phaseCount: number;
  videoCount: number;
}

const PLAN_LABELS: Record<string, string> = {
  starter: "Individual Leaflet",
  pro:     "Leaflet Package",
  elite:   "Unlimited Monthly Access",
};

const PLAN_PRICES: Record<string, { monthly: number; yearly: number }> = {
  starter: { monthly: 12, yearly: 12 },
  pro:     { monthly: 30, yearly: 30 },
  elite:   { monthly: 50, yearly: 50 },
};

const SubscribePlan: React.FC = () => {
  const [params]   = useSearchParams();
  const navigate   = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const planId    = params.get("plan")    ?? "starter";
  const billing   = params.get("billing") ?? "monthly";
  const courseIds = (params.get("courses") ?? "").split(",").filter(Boolean);
  // Only Elite gives unlimited access; starter/pro require leaflet selection
  const isFullAccess = planId === "elite";

  const [courses,    setCourses]    = useState<CourseInfo[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState("");
  const [done,       setDone]       = useState(false);
  const [lbpSettings, setLbpSettings] = useState<LbpSettings>(DEFAULT_LBP_SETTINGS);

  useEffect(() => { getLbpSettings().then(setLbpSettings); }, []);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`);
    }
  }, [isAuthenticated, navigate]);

  // Load course details for selected IDs (Elite skips — all courses included)
  useEffect(() => {
    if (isFullAccess || !courseIds.length) { setLoading(false); return; }
    setLoading(true);
    apiClient.get("/courses", { params: { limit: 100 } })
      .then(res => {
        const all: CourseInfo[] = res.data.courses || [];
        setCourses(all.filter(c => courseIds.includes(c._id)));
      })
      .catch(() => setCourses([]))
      .finally(() => setLoading(false));
  }, [params.toString()]);

  const price    = PLAN_PRICES[planId]?.[billing as "monthly" | "yearly"] ?? 29;
  const planName = PLAN_LABELS[planId] ?? planId;

  const handleSubscribe = async () => {
    setSubmitting(true);
    setError("");
    try {
      if (courseIds.length > 0) {
        // Check if user already has an active subscription
        const subRes = await apiClient.get("/subscriptions/me").catch(() => null);
        const hasActive = subRes?.data?.hasActiveSubscription;

        if (hasActive) {
          // Add each new course to the existing subscription instead of creating a new one
          for (const courseId of courseIds) {
            await apiClient.patch("/subscriptions/me/add-course", { courseId });
          }
          setDone(true);
          setTimeout(() => navigate("/membership/Classes"), 2500);
          return;
        }
      }

      // No existing subscription — create a fresh one
      await apiClient.post("/subscriptions", {
        plan:              planId === "starter" ? "basic" : planId === "pro" ? "premium" : "pro",
        billingCycle:      "one-time",
        price,
        selectedCourseIds: courseIds,
        features:          [],
        metadata:          { isPreRecorded: true, fromPricingPage: true },
      });
      setDone(true);
      setTimeout(() => navigate("/membership/Classes"), 2500);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Subscription failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return (
    <Box sx={{ minHeight: "100vh", bgcolor: BG, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Box sx={{ textAlign: "center", px: 3 }}>
        <Box sx={{
          width: 88, height: 88, borderRadius: "50%", mx: "auto", mb: 3,
          bgcolor: "#ECFDF5", display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <CheckCircle sx={{ fontSize: 48, color: "#10B981" }} />
        </Box>
        <Typography variant="h4" fontWeight={800} sx={{ color: NAVY, mb: 1 }}>
          You're all set! 🎉
        </Typography>
        <Typography sx={{ color: MUTED, mb: 2 }}>
          Your {planName} plan is active. Redirecting to your courses...
        </Typography>
        <CircularProgress size={24} sx={{ color: BLUE }} />
      </Box>
    </Box>
  );

  return (
    <>
    <Helmet>
      <title>Complete Your Enrollment — Dr.Paw</title>
      <meta name="description" content="You're one step away from vet-written pet-care courses and expert guides. Complete your Dr.Paw enrollment now." />
      <link rel="canonical" href="https://drpawleb.com/subscribe-plan" />
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
    <HreflangTags />
    <Box sx={{ minHeight: "100vh", bgcolor: BG, py: { xs: 4, md: 8 } }}>
      <Container maxWidth="sm">

        {/* Back */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ textTransform: "none", color: MUTED, mb: 3, fontWeight: 600 }}
        >
          Back
        </Button>

        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Chip
            label={`${planName} Plan`}
            sx={{ bgcolor: "rgba(21,101,192,.1)", color: BLUE, fontWeight: 700, mb: 1.5 }}
          />
          <Typography variant="h4" fontWeight={900} sx={{ color: NAVY, letterSpacing: "-.02em", mb: .5 }}>
            {isFullAccess ? "Confirm Your Subscription" : "Confirm Your Purchase"}
          </Typography>
          <Typography sx={{ color: MUTED }}>
            {isFullAccess ? "Review your plan and complete your subscription" : "Review your selected leaflets and complete your purchase"}
          </Typography>
        </Box>

        {/* Order summary card */}
        <Paper elevation={0} sx={{ borderRadius: "20px", border: "1px solid #E8EDF5", overflow: "hidden", mb: 2.5 }}>
          {/* Plan summary */}
          <Box sx={{ p: 3, bgcolor: "#fff" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Box>
                <Typography fontWeight={800} sx={{ color: NAVY }}>{planName} Plan</Typography>
                <Typography variant="caption" sx={{ color: MUTED, textTransform: "capitalize" }}>
                  {isFullAccess ? `Billed ${billing}` : "One-time payment"}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="h5" fontWeight={800} sx={{ color: BLUE, lineHeight: 1 }}>
                  ${price}
                </Typography>
                {lbpSettings.showLbp && (
                  <Typography variant="caption" sx={{ color: MUTED }}>
                    {formatLbpOnly(price, lbpSettings)}
                  </Typography>
                )}
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Selected courses */}
            <Typography variant="caption" fontWeight={700} sx={{ color: MUTED, textTransform: "uppercase", letterSpacing: ".06em", display: "block", mb: 1.5 }}>
              {isFullAccess ? "What's Included" : `Your Selected Leaflet${courseIds.length > 1 ? "s" : ""}`}
            </Typography>

            {isFullAccess ? (
              /* Elite — all courses */
              <Box sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                p: 1.5, borderRadius: "12px",
                background: "linear-gradient(135deg, rgba(21,101,192,0.08), rgba(66,165,245,0.08))",
                border: "1px solid rgba(21,101,192,.2)",
              }}>
                <Box sx={{
                  width: 48, height: 36, borderRadius: "8px", flexShrink: 0,
                  background: `linear-gradient(135deg, ${BLUE}, #42A5F5)`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <AutoAwesome sx={{ color: "#fff", fontSize: 18 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={700} fontSize=".88rem" sx={{ color: NAVY }}>
                    Full Educational Library — Unlimited Access
                  </Typography>
                  <Typography variant="caption" sx={{ color: MUTED }}>
                    All current and future leaflets included · 1 private consultation
                  </Typography>
                </Box>
                <CheckCircle sx={{ color: "#10B981", fontSize: 18, flexShrink: 0 }} />
              </Box>
            ) : loading ? (
              <Stack spacing={1.5}>
                {courseIds.map((_, i) => (
                  <Box key={i} sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                    <Box sx={{ width: 48, height: 36, borderRadius: "8px", bgcolor: "#EEF4FF" }} />
                    <Box sx={{ flex: 1, height: 14, borderRadius: "4px", bgcolor: "#EEF4FF" }} />
                  </Box>
                ))}
              </Stack>
            ) : courses.length === 0 ? (
              <Alert severity="warning" sx={{ borderRadius: "12px" }}>
                No leaflets selected. Please go back and choose a leaflet.
              </Alert>
            ) : (
              <Stack spacing={1.5}>
                {courses.map(course => (
                  <Box key={course._id} sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    p: 1.5, borderRadius: "12px",
                    bgcolor: "#EEF4FF", border: "1px solid rgba(21,101,192,.12)",
                  }}>
                    <Avatar
                      src={course.thumbnail}
                      variant="rounded"
                      sx={{ width: 48, height: 36, borderRadius: "8px", bgcolor: "#DDEAFF", flexShrink: 0 }}
                    >
                      <School sx={{ fontSize: 18, color: BLUE }} />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={700} fontSize=".88rem" sx={{ color: NAVY }} noWrap>
                        {course.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: MUTED }}>
                        {course.phaseCount} phases · {course.videoCount} videos
                      </Typography>
                    </Box>
                    <CheckCircle sx={{ color: "#10B981", fontSize: 18, flexShrink: 0 }} />
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          {/* Total */}
          <Box sx={{ px: 3, py: 2, bgcolor: "#F8FBFF", borderTop: "1px solid #E8EDF5" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography fontWeight={700} sx={{ color: NAVY }}>Total due today</Typography>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="h6" fontWeight={800} sx={{ color: BLUE }}>
                  ${price}{isFullAccess ? "/mo" : ""}
                </Typography>
                {lbpSettings.showLbp && (
                  <Typography variant="caption" sx={{ color: MUTED, display: "block" }}>
                    {formatLbpOnly(price, lbpSettings)}
                  </Typography>
                )}
              </Box>
            </Box>
            {billing === "yearly" && (
              <Typography variant="caption" sx={{ color: "#10B981", fontWeight: 600 }}>
                Billed annually — save {Math.round(((PLAN_PRICES[planId]?.monthly - price) / PLAN_PRICES[planId]?.monthly) * 100)}% vs monthly
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Payment placeholder */}
        <Paper elevation={0} sx={{ borderRadius: "20px", border: "1px solid #E8EDF5", p: 3, mb: 2.5, bgcolor: "#fff" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <CreditCard sx={{ color: BLUE }} />
            <Typography fontWeight={700} sx={{ color: NAVY }}>Payment</Typography>
          </Box>
          <Box sx={{
            p: 2.5, borderRadius: "12px", bgcolor: BG, border: "1px dashed #BBDEFB",
            textAlign: "center",
          }}>
            <LockOutlined sx={{ color: MUTED, fontSize: 28, mb: 1 }} />
            <Typography variant="body2" sx={{ color: MUTED }}>
              Secure payment powered by Stripe
            </Typography>
            <Typography variant="caption" sx={{ color: MUTED }}>
              Your card details are encrypted and never stored on our servers
            </Typography>
          </Box>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{error}</Alert>}

        {/* Subscribe button */}
        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={submitting || (!isFullAccess && (courses.length === 0 || loading))}
          onClick={handleSubscribe}
          endIcon={submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <ArrowForward />}
          sx={{
            textTransform: "none", fontWeight: 800, fontSize: "1rem",
            borderRadius: "50px", py: 1.8,
            bgcolor: BLUE, boxShadow: "0 6px 20px rgba(21,101,192,.35)",
            "&:hover": { bgcolor: NAVY },
            "&.Mui-disabled": { bgcolor: "#E8EDF5", color: "#90A4AE" },
          }}
        >
          {submitting ? "Processing..." : isFullAccess ? `Subscribe — $${price}` : `Purchase — $${price}`}
        </Button>
      </Container>
    </Box>
    </>
  );
};

export default SubscribePlan;
